"use client";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { Alert, Box, Button, Container, Skeleton, Typography } from "@mui/material";
import DateIcon from "@mui/icons-material/TodayRounded";
import LocationIcon from "@mui/icons-material/FmdGoodOutlined";
import ShipIcon from "@mui/icons-material/DirectionsBoatFilledRounded";
import AdsClickIcon from "@mui/icons-material/AdsClickRounded";
import { IAllCruisesList, ICruise } from "@/interfaces/IAllCruisesList";
import { convertToDateV1 } from "@/helpers/helpers";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { toKebabCase } from "@/utils/helpers";
import { fetchMoreCruises } from "../cruise-results/miniComponents/loadmore_request";

const BestCruiseDeals = () => {
  const [loading, setLoading] = useState(true);
  const [progressiveDeals, setProgressiveDeals] = useState<
    Array<ICruise & { region: string; lowestPrice: number }>
  >([]);
  const [loadedRegionsCount, setLoadedRegionsCount] = useState(0);
  const [finalDeals, setFinalDeals] = useState<
    Array<ICruise & { region: string; lowestPrice: number }>
  >([]);

  const regions = [
    "Transatlantic",
    "Panama Canal",
    "Alaska",
    "Middle East",
    "West Coast US",
    "Caribbean",
    // "Mediterranean",
    // "Northern Europe",
    // "Pacific",
    // "Arctic",
    // "Antarctica",
    // "Indian Ocean",
    // "Repositioning",
    // "Western Europe",
    // "World-wide",
  ];

  // Progressive loading effect
  useEffect(() => {
    const loadRegionsProgressively = async () => {
      const loadedDeals: Array<ICruise & { region: string; lowestPrice: number }> = [];

      for (let i = 0; i < regions.length; i++) {
        const region = regions[i];

        try {
          const apiParams = {
            region: region,
            limit: 15,
            page: 1,
            profileImageGeometry: "300x",
            coverImageGeometry: "800x",
          };

          // Wait for API response before continuing
          const response: IAllCruisesList = await fetchMoreCruises(apiParams);

          if (response.cruises && response.cruises.length > 0) {
            // Find the cruise with lowest price in this region
            const regionMinDeal = getMinimumPriceFromRegion(response.cruises, region);
            if (regionMinDeal) {
              loadedDeals.push(regionMinDeal);
              // Update state immediately after getting response
              setProgressiveDeals([...loadedDeals]);
              setLoadedRegionsCount(i + 1);
            }
          }
        } catch (err) {
          console.log(`Error fetching ${region}:`, err);
        }
      }

      // Final sort by lowest price and set final state
      const sortedDeals = loadedDeals.sort((a, b) => a.lowestPrice - b.lowestPrice);
      setFinalDeals(sortedDeals);
      setLoading(false);
    };

    loadRegionsProgressively();
  }, []);

  // Display deals - show progressive loading
  const displayDeals = useMemo(() => {
    if (loading) {
      // During loading, show progressive deals
      return progressiveDeals;
    }
    // After loading is complete,
    return finalDeals.slice(0, 5);
  }, [loading, progressiveDeals, finalDeals]);

  return (
    <Box
      sx={{
        mt: { xs: "1rem", md: "2rem" },
        overflow: { xs: "hidden", md: "visible" },
        mb: "3rem",
      }}
    >
      <Container>
        <Box sx={{ position: "relative" }}>
          <Box sx={{ position: "relative", zIndex: 2 }}>
            <Box sx={{ mt: "1rem" }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2.2rem", md: "3rem" },
                  fontWeight: { xs: 800, md: 700 },
                  color: "primary.main",
                  lineHeight: { xs: 1.1, md: 1.3 },
                  mb: { xs: "10px", md: "0px" },
                  span: {
                    color: "transparent",
                    WebkitTextStrokeColor: "#CE0000",
                    WebkitTextStrokeWidth: "1px",
                  },
                }}
              >
                Best <span>Cruise</span> Deals
              </Typography>
              <Box
                sx={{
                  fontSize: { xs: "14px", md: "1rem" },
                  fontWeight: 400,
                  lineHeight: { xs: 1.4, md: 1.3 },
                  mb: { xs: "1.4rem", md: "1.2rem" },
                }}
              >
                Discover our exclusive deals on some of the best cruises around the world. Book
                early to secure your spot and enjoy special offers!
              </Box>

              {/* Progress indicator */}
              {loading && (
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: "4px",
                      bgcolor: "#EEF5FF",
                      borderRadius: "2px",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${(loadedRegionsCount / regions.length) * 100}%`,
                        height: "100%",
                        background:
                          "linear-gradient(90deg,   #9031e9ff, #ff9dceff, #ff60ffff, #ff9dceff, #9031e9ff)",
                        backgroundSize: "200% 100%",
                        transition: "width 0.3s ease",
                        borderRadius: "2px",
                        animation: "rainbow-slide 2s linear infinite",
                        "@keyframes rainbow-slide": {
                          "0%": { backgroundPosition: "0% 50%" },
                          "100%": { backgroundPosition: "200% 50%" },
                        },
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: "20px",
                  "> div": {
                    width: "100%",
                    height: "100%",
                    "&:nth-of-type(1)": {
                      gridRow: { lg: "1/3" },
                      minHeight: "500px",
                    },
                    "&:nth-of-type(2)": {
                      gridRow: { lg: "1/2" },
                      minHeight: "250px",
                      maxHeight: { md: "unset", lg: "260px" },
                    },
                    "&:nth-of-type(3)": {
                      gridRow: { lg: "2/3" },
                      minHeight: "250px",
                      maxHeight: "260px",
                    },
                  },
                }}
              >
                {Array.from({ length: 5 }).map((_, index) => {
                  // Take only the first 5 deals to ensure exactly 5 items
                  const cruise = displayDeals[index];

                  if (cruise) {
                    return (
                      <BestCruiseCard
                        key={`${cruise.region}-${cruise.ref}`}
                        image={(index + 1).toString()}
                        cruise={cruise}
                        isLargeCard={index === 0}
                      />
                    );
                  }

                  // Show skeleton for remaining slots
                  return (
                    <Box
                      key={`skeleton-${index}`}
                      sx={{
                        height: "218px",
                        width: "100%",
                        position: "relative",
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        sx={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "15px",
                          minheight: "218px",
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>

              {!loading && finalDeals.length === 0 && (
                <Box>
                  <Alert severity="error">No data available. Please contact our support</Alert>
                </Box>
              )}
            </Box>

            {/* Mobile view with Swiper */}
            <Box sx={{ display: { xs: "block", md: "none" } }}>
              {displayDeals?.length > 0 && (
                <Box
                  sx={{
                    position: "relative",
                    width: { xs: "100%", sm: "100%" },
                    ".swiper": {
                      width: "100%",
                      overflow: { xs: "visible", sm: "hidden" },
                    },
                  }}
                >
                  <Swiper
                    spaceBetween={16}
                    slidesPerView={4}
                    breakpoints={{
                      0: {
                        slidesPerView: 1.1,
                      },
                      600: {
                        slidesPerView: 2,
                      },
                      900: {
                        slidesPerView: 3,
                      },
                      1200: {
                        slidesPerView: 4,
                      },
                    }}
                  >
                    {displayDeals.map((cruise, cI) => (
                      <SwiperSlide key={`${cruise.region}-${cruise.ref}`}>
                        <BestCruiseCard
                          image={(cI + 1)?.toString()}
                          cruise={cruise}
                          isLargeCard={cI === 0}
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </Box>
              )}

              {displayDeals.length === 0 && loading && (
                <Box
                  sx={{
                    height: "55vh",
                    width: "100%",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "row",
                    gap: "16px",
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{
                      width: "calc(100% - 10vw)",
                      height: "100%",
                      borderRadius: "20px",
                      minHeight: "400px",
                    }}
                  />
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{
                      width: "10vw",
                      height: "100%",
                      borderRadius: "20px 0px 0px 20px",
                      minHeight: "400px",
                    }}
                  />
                </Box>
              )}
              {!loading && finalDeals.length === 0 && <Box>No data available</Box>}
            </Box>
          </Box>

          <Box
            sx={{
              bgcolor: "#EEF5FF",
              position: "absolute",
              top: { xs: "-14px", lg: "-1.25rem", xl: "-1.5rem" },
              left: { xs: "-16px", sm: "-24px", md: "-24px", lg: "-1rem", xl: "-2.5rem" },
              width: {
                xs: "calc(100% + 32px)",
                sm: "calc(100% + 48px)",
                md: "calc(100% + 3rem)",
                lg: "calc(100% + 2rem)",
                xl: "calc(100% + 5rem)",
              },
              height: {
                xs: "calc(100% + 32px)",
                lg: "calc(100% + 2.5rem)",
                xl: "calc(100% + 3.5rem)",
              },
              zIndex: 1,
              borderRadius: { xs: "0px", lg: "35px" },
            }}
          ></Box>
        </Box>
      </Container>
    </Box>
  );
};

export default BestCruiseDeals;

// to get minimum price deal from a region's cruise
const getMinimumPriceFromRegion = (cruises: ICruise[], region: string) => {
  if (!cruises || cruises.length === 0) return null;

  let regionMinDeal = null;
  let regionMinPrice = Infinity;

  cruises.forEach((cruise) => {
    const allPrices = extractPricesFromCruise(cruise);
    if (allPrices.length > 0) {
      const sortedPrices = [...allPrices].sort((a, b) => a.price - b.price);
      const lowestPriceObj = sortedPrices[0];

      if (lowestPriceObj.price < regionMinPrice) {
        regionMinPrice = lowestPriceObj.price;
        regionMinDeal = {
          ...cruise,
          region,
          lowestPrice: lowestPriceObj.price,
          lowestPriceType: lowestPriceObj.type,
          lowestPriceDisplayName: lowestPriceObj.displayName,
          allPrices: sortedPrices,
        };
      }
    }
  });

  return regionMinDeal;
};

const BestCruiseCard: React.FC<{
  cruise: ICruise & { region?: string };
  isLargeCard?: boolean;
  image: string;
}> = ({ cruise, isLargeCard }) => {
  const priceInfo = useMemo(() => {
    return getLowestPriceInfo(cruise);
  }, [cruise]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: "#fff",
        p: { xs: "0px", md: "16px" },
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr", lg: "250px auto", xl: "300px auto" },
        gap: { xs: "0px", md: "1rem" },
        borderRadius: { xs: "20px", md: "24px" },
        border: "1px solid #A8C3EA",
        boxShadow: "0px 0px 16px rgba(0,0,0,0.12)",
        ...(isLargeCard && {
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: { xs: "0px", md: "1rem", lg: "10px" },
        }),
      }}
    >
      <Box
        sx={{
          width: "100%",
          minHeight: { xs: "220px", md: "200px" },
          height: { xs: "200px", md: "auto" },
          ...(isLargeCard && {
            minHeight: { xs: "220px", md: "200px", lg: "300px" },
          }),
          borderRadius: { xs: "15px 15px 0 0", md: "15px" },
          overflow: "hidden",
          position: "relative",
          img: {
            objectFit: "cover",
            height: "100%",
            width: "100%",
            position: "absolute",
            inset: "0px",
          },
        }}
      >
        {cruise?.shipImageHref && (
          <Image
            src={cruise?.shipImageHref}
            alt={cruise?.shipTitle}
            width={50}
            height={10}
            unoptimized
            className="panning_image"
          />
        )}

        <Box
          sx={{
            p: "5px 11px",
            bgcolor: "#E30053",
            color: "#fff",
            textTransform: "capitalize",
            width: "fit-content",
            fontFamily: "'blauerNue', 'blauerNue Fallback'",
            fontWeight: 600,
            fontSize: "14px",
            borderRadius: "15px",
            position: "absolute",
            bottom: "10px",
            left: "10px",
            pointerEvents: "none",
          }}
        >
          {cruise?.cruiseType} Cruise
        </Box>

        {/* Region badge */}
        {cruise?.region && (
          <Box
            sx={{
              p: "5px 11px",
              bgcolor: "#7D46C9",
              color: "#fff",
              textTransform: "capitalize",
              width: "fit-content",
              fontFamily: "'blauerNue', 'blauerNue Fallback'",
              fontWeight: 600,
              fontSize: "12px",
              borderRadius: "15px",
              position: "absolute",
              top: "10px",
              right: "10px",
            }}
          >
            {cruise.region}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          minHeight: { xs: "295px", md: "auto" },
          p: { xs: "1rem", md: "0px" },
        }}
      >
        <Box>
          <Box
            sx={{
              fontSize: { xs: "1.4rem", md: "1.4rem", lg: "1.4rem" },
              fontFamily: "'blauerNue', 'blauerNue Fallback'",
              fontWeight: { xs: 700, md: 700 },
              mb: "10px",
              ...(isLargeCard && {
                fontSize: { xs: "1.4rem", md: "1.4rem", lg: "1.9rem" },
              }),
            }}
            component="h3"
          >
            {cruise.name}
            {"  "}
            <Box
              component="span"
              sx={{
                bgcolor: "#FFBB00",
                color: "#350097",
                fontSize: "14px",
                p: "3px 5px 3px 4px",
                textTransform: "uppercase",
                borderRadius: "6px",
                border: "1px solid #5900FF80",
                ml: "0px",
                position: "relative",
                top: "-3px",
                ...(isLargeCard && {
                  top: "-6px",
                }),
              }}
            >
              {cruise?.cruiseNights} Nights
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: "10px",
            }}
          >
            <Box>
              <Box
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#7D46C9",
                  fontFamily: "'blauerNue', 'blauerNue Fallback'",
                  mb: "4px",
                }}
              >
                Departing From
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "3px",
                  fontSize: "14px",
                  color: "#666",
                  fontWeight: 400,
                }}
              >
                <LocationIcon sx={{ width: "20px", height: "20px", ml: "-3px" }} />
                <Box sx={{ mt: "2px" }}>{cruise?.startsAt}</Box>
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: "flex", gap: "4px", color: "#7D46C9" }}>
                <Box>
                  <Box
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#7D46C9",
                      fontFamily: "'blauerNue', 'blauerNue Fallback'",
                      mb: "4px",
                    }}
                  >
                    Departing On
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "3px",
                      fontSize: "14px",
                      color: "#666",
                      fontWeight: 400,
                    }}
                  >
                    <DateIcon sx={{ width: "20px", height: "20px", ml: "-2px" }} />
                    <Box sx={{ mt: "2px" }}>{convertToDateV1(cruise?.startsOn)}</Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box>
            <Box
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: "#7D46C9",
                fontFamily: "'blauerNue', 'blauerNue Fallback'",
                mb: "4px",
                mt: "8px",
              }}
            >
              Sailing on
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                fontSize: "14px",
                gap: "3px",
                color: "#666",
                ml: "-2px",
                mb: "2px",
                svg: {
                  width: "20px",
                  height: "20px",
                  color: "#555",
                },
                span: {
                  fontWeight: 700,
                },
              }}
            >
              <ShipIcon />
              {cruise?.shipTitle} by <span>{cruise?.operatorTitle}</span>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            width: "100%",
            borderTop: "1px solid #ccc",
            pt: "2px",
            mt: "8px",
          }}
        >
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                fontSize: { xs: "12px", md: "14px" },
                fontWeight: 700,
                fontFamily: "'blauerNue', 'blauerNue Fallback'",
                mt: "8px",
                lineHeight: 1.2,
              }}
            >
              PRICE FROM
            </Box>
            <Typography
              variant="body2"
              sx={{
                mt: "-3px",
                color: "#E30053",
                fontSize: "25px",
                fontWeight: 600,
                fontFamily: "'blauerNue', 'blauerNue Fallback'",
                lineHeight: 1.1,
                span: {
                  color: "#A8215C",
                  fontSize: "22px",
                },
                ".gbp": {
                  mr: "2px",
                },
                ".pp": {
                  ml: "2px",
                },
              }}
            >
              <span className="gbp">£</span>
              {priceInfo.price}
              <span className="pp">pp</span>

              {priceInfo.displayName && (
                <Box
                component={'span'}
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    position: "absolute",
                    top: { xs: "10px", md: "9px" },
                    left: { xs: "88px", md: "95px" },
                    width: "max-content",
                    color: "#060273",
                  }}
                >
                  {priceInfo.displayName}
                </Box>
              )}
            </Typography>
          </Box>
          <LinkButtonV2
            href={`/cruise-results/${toKebabCase(cruise?.name ?? "")}?cruiseRef=${
              cruise?.ref
            }&shipRef=${cruise?.ship?.split("/").pop()?.split(".")[0] ?? ""}`}
            text="View Itinerary"
            icon={
              <AdsClickIcon
                className="zoom-icon"
                sx={{ display: { xs: "none", md: "inline-block" } }}
              />
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

const extractPricesFromCruise = (cruise: ICruise) => {
  return [
    { price: Number(cruise.cruiseOnlyPrice), type: "Cruise Only", displayName: "" },
    { price: Number(cruise.flyCruisePrice), type: "Fly Cruise", displayName: "(with Flight)" },
    { price: Number(cruise.insidePrice), type: "Inside Cabin", displayName: "(Inside Cabin)" },
    { price: Number(cruise.outsidePrice), type: "Outside Cabin", displayName: "(Outside Cabin)" },
    { price: Number(cruise.balconyPrice), type: "Balcony", displayName: "(Balcony)" },
    { price: Number(cruise.suitePrice), type: "Suite", displayName: "(Suite)" },
  ].filter((p) => p.price > 0 && !isNaN(p.price));
};

const getLowestPriceInfo = (cruise: ICruise) => {
  const prices = extractPricesFromCruise(cruise);
  if (prices.length === 0) {
    return { price: "0.00", type: "", displayName: "" };
  }
  const sortedPrices = [...prices].sort((a, b) => a.price - b.price);
  const lowest = sortedPrices[0];
  return {
    price: lowest.price.toFixed(2),
    type: lowest.type,
    displayName: lowest.displayName,
  };
};

interface LinkButtonV2Props {
  href: string;
  text: string;
  icon?: ReactNode;
}

export const LinkButtonV2: React.FC<LinkButtonV2Props> = ({ href, text, icon }) => {
  const handleClick = () => {
    window.location.href = href;
  };
  return (
    <Button
      onClick={handleClick}
      sx={{
        textTransform: "initial",
        gap: "5px",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        p: "6px 16px",
      }}
    >
      <Box>{text}</Box>
      <Box sx={{ display: "flex", alignItems: "center" }}> {icon}</Box>
    </Button>
  );
};
